import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, X, Loader2, CalendarX } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { supabase } from '@/lib/supabase';
import {
  useAppointment,
  useCancelAppointment,
  useCompleteAppointment,
  useMarkNoShowAppointment,
  useCreateAppointment,
  useCreateRecurringAppointment,
  useRescheduleAppointment,
  useUpdateAppointmentNotes,
  useDeleteAppointmentSeries,
  useUpdateAppointmentSeries,
} from '@/hooks/useAppointments';
import { useServicesList } from '@/hooks/useServices';
import { useClinicMembers } from '@/hooks/useClinicMembers';
import { usePatientPicker, type PatientPickerItem } from '@/hooks/usePatientPicker';
import { usePatient } from '@/hooks/usePatients';
import { Button } from '@/components/ui/Button';
import {
  RecurrenceForm,
  type RecurrenceFormData,
} from '@/components/appointments/RecurrenceForm';
import { AvailabilityPicker } from '@/components/appointments/AvailabilityPicker';

export interface AppointmentModalProps {
  clinicId: string;
  mode: 'create' | 'edit';
  appointmentId?: string | null;
  /** Initial slot when creating (clicked time slot in the calendar). */
  initialStart?: Date | null;
  /** Optional explicit end time when creating from a drag-range selection. */
  initialEnd?: Date | null;
  initialClinicianId?: string | null;
  /** Pre-select patient when creating from a patient detail page. */
  initialPatientId?: string | null;
  onClose: () => void;
}

function isoLocal(d: Date): string {
  // Format Date as `YYYY-MM-DDTHH:mm` for <input type="datetime-local">
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

function fromInputValue(value: string): Date {
  // <input type="datetime-local"> returns wall-clock time in user's locale.
  return new Date(value);
}

export function AppointmentModal(props: AppointmentModalProps) {
  const { t } = useTranslation();
  const {
    clinicId,
    mode,
    appointmentId,
    initialStart,
    initialEnd,
    initialClinicianId,
    initialPatientId,
    onClose,
  } = props;

  const { data: existing, isLoading: loadingExisting } = useAppointment(
    mode === 'edit' ? appointmentId : null,
  );
  const { data: initialPatient } = usePatient(
    mode === 'create' ? (initialPatientId ?? null) : null,
  );
  const { data: existingPatient } = usePatient(
    mode === 'edit' && existing ? existing.patient_id : null,
  );
  const { data: services } = useServicesList(clinicId, { includeInactive: false });
  const { data: members } = useClinicMembers(clinicId);

  const create = useCreateAppointment();
  const createRecurring = useCreateRecurringAppointment();
  const reschedule = useRescheduleAppointment();
  const updateNotes = useUpdateAppointmentNotes();
  const cancel = useCancelAppointment();
  const complete = useCompleteAppointment();
  const markNoShow = useMarkNoShowAppointment();
  const deleteSeries = useDeleteAppointmentSeries();
  const updateSeries = useUpdateAppointmentSeries();

  const [patient, setPatient] = useState<PatientPickerItem | null>(null);
  const [serviceId, setServiceId] = useState<string | null>(null);
  
  // Check if selected service is a blocker (computed after serviceId is declared)
  const selectedService = services?.find((s) => s.id === serviceId);
  const isBlockerService = Boolean(selectedService?.is_blocker);
  const [clinicianId, setClinicianId] = useState<string | null>(initialClinicianId ?? null);
  const [startsAt, setStartsAt] = useState<string>('');
  const [endsAt, setEndsAt] = useState<string>('');
  const [selectedDateTime, setSelectedDateTime] = useState<Date | null>(null);
  const [notes, setNotes] = useState<string>('');
  const [showAvailabilityPicker, setShowAvailabilityPicker] = useState(false);
  const [recurrence, setRecurrence] = useState<RecurrenceFormData>({
    pattern: 'none',
    endType: 'count',
    endDate: null,
    count: 4,
  });
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelScope, setCancelScope] = useState<'single' | 'all' | 'future'>('single');

  const isRecurringSeries =
    existing?.recurrence_pattern !== 'none' &&
    Boolean(existing?.is_recurrence_parent || existing?.recurrence_parent_id);
  const canCancelAppointment = mode === 'edit' && existing?.status === 'upcoming';

  // Hydrate state when editing
  useEffect(() => {
    if (mode !== 'edit' || !existing) return;
    setServiceId(existing.service_id ?? null);
    setClinicianId(existing.clinician_id ?? null);
    const existingStart = new Date(existing.starts_at);
    setStartsAt(isoLocal(existingStart));
    setEndsAt(isoLocal(new Date(existing.ends_at)));
    setSelectedDateTime(existingStart);
    setNotes(existing.notes ?? '');
  }, [mode, existing]);

  // Hydrate patient when editing — wait for patient data so the name displays correctly
  useEffect(() => {
    if (mode !== 'edit' || !existing || !existing.patient_id || !existingPatient) return;
    setPatient({
      id: existing.patient_id,
      pat_number: existingPatient.pat_number,
      first_name: existingPatient.first_name,
      last_name: existingPatient.last_name,
      email: existingPatient.email,
      phone: existingPatient.phone,
    });
  }, [mode, existing, existingPatient]);

  // Initial slot for create mode
  useEffect(() => {
    if (mode !== 'create') return;
    const start = initialStart ?? new Date();
    setStartsAt(isoLocal(start));
    setSelectedDateTime(start);
    const defaultEnd = initialEnd ?? new Date(start.getTime() + 30 * 60_000);
    setEndsAt(isoLocal(defaultEnd));
  }, [mode, initialStart, initialEnd]);

  // Pre-select patient when opened from patient detail
  useEffect(() => {
    if (mode !== 'create' || !initialPatient) return;
    setPatient({
      id: initialPatient.id,
      pat_number: initialPatient.pat_number,
      first_name: initialPatient.first_name,
      last_name: initialPatient.last_name,
      email: initialPatient.email,
      phone: initialPatient.phone,
    });
  }, [mode, initialPatient]);

  // Auto-extend end-time when service duration changes
  function applyServiceDuration(svcId: string | null) {
    setServiceId(svcId);
    if (!svcId || !startsAt) return;
    const svc = services?.find((s) => s.id === svcId);
    if (!svc) return;
    const startDate = fromInputValue(startsAt);
    const endDate = new Date(startDate.getTime() + svc.duration_minutes * 60_000);
    setEndsAt(isoLocal(endDate));
  }

  // Handle availability picker selection
  function handleAvailabilitySelect(dateTime: Date, newClinicianId: string | null) {
    setSelectedDateTime(dateTime);
    setStartsAt(isoLocal(dateTime));
    
    // Calculate end time based on service duration
    const svc = services?.find((s) => s.id === serviceId);
    const durationMinutes = svc?.duration_minutes ?? 30;
    const endTime = new Date(dateTime.getTime() + durationMinutes * 60_000);
    setEndsAt(isoLocal(endTime));
    
    if (newClinicianId) {
      setClinicianId(newClinicianId);
    }
  }

  // Get duration for availability picker
  const appointmentDuration = useMemo(() => {
    const svc = services?.find((s) => s.id === serviceId);
    return svc?.duration_minutes ?? 30;
  }, [serviceId, services]);

  // Re-anchor end-time if user moves the start time
  function applyStart(value: string) {
    setStartsAt(value);
    if (!value) return;
    const svc = services?.find((s) => s.id === serviceId);
    if (svc) {
      const startDate = fromInputValue(value);
      const endDate = new Date(startDate.getTime() + svc.duration_minutes * 60_000);
      setEndsAt(isoLocal(endDate));
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!startsAt || !endsAt) {
      toast.error(t('appointments.errors.invalid_time'));
      return;
    }

    const startISO = fromInputValue(startsAt).toISOString();
    const endISO = fromInputValue(endsAt).toISOString();
    if (new Date(endISO) <= new Date(startISO)) {
      toast.error(t('appointments.errors.invalid_time'));
      return;
    }

    try {
      if (mode === 'create') {
        // For blockers, patient is not required
        if (!isBlockerService && !patient) {
          toast.error(t('appointments.errors.no_patient'));
          return;
        }

        // Check if recurring
        if (recurrence.pattern !== 'none') {
          // Validate recurrence end condition
          if (!recurrence.endDate && !recurrence.count) {
            toast.error(t('appointments.errors.recurrence_end_required'));
            return;
          }

          const result = await createRecurring.mutateAsync({
            clinic_id: clinicId,
            patient_id: patient?.id ?? null,
            starts_at: startISO,
            ends_at: endISO,
            service_id: serviceId,
            clinician_id: clinicianId,
            notes: notes.trim() ? notes : null,
            recurrence_pattern: recurrence.pattern as 'daily' | 'weekly' | 'biweekly' | 'monthly',
            recurrence_end_date: recurrence.endDate || null,
            recurrence_count: recurrence.count || null,
          });
          toast.success(t('appointments.toasts.series_created', { count: result.length }));
        } else {
          const result = await create.mutateAsync({
            clinic_id: clinicId,
            patient_id: patient?.id ?? null,
            starts_at: startISO,
            ends_at: endISO,
            service_id: serviceId,
            clinician_id: clinicianId,
            notes: notes.trim() ? notes : null,
          });
          
          // Send confirmation email (fire-and-forget) - only if there's a patient
          if (patient) {
            supabase.functions
              .invoke('send-appointment-confirmation', {
                body: { appointment_id: result.id },
              })
              .catch((err) => {
                console.error('Failed to send confirmation email:', err);
              });
          }
          
          toast.success(t('appointments.toasts.created'));
        }
      } else if (existing) {
        // Reschedule (always re-sends times even if unchanged)
        await reschedule.mutateAsync({
          appointment_id: existing.id,
          starts_at: startISO,
          ends_at: endISO,
          clinician_id: clinicianId,
        });
        if ((existing.notes ?? '') !== notes) {
          await updateNotes.mutateAsync({
            appointment_id: existing.id,
            notes,
          });
        }
        toast.success(t('appointments.toasts.updated'));
      }
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'unknown';
      if (message.includes('conflict:')) {
        toast.error(t('appointments.errors.conflict'));
      } else {
        toast.error(t('appointments.errors.unknown'));
      }
      console.error('appointment save failed', err);
    }
  }

  function openCancelConfirm() {
    setCancelReason('');
    setCancelScope('single');
    setShowCancelConfirm(true);
  }

  async function onCancelConfirm() {
    if (!existing) return;

    const reason = cancelReason.trim() ? cancelReason.trim() : null;

    try {
      if (isRecurringSeries && cancelScope !== 'single') {
        const count = await deleteSeries.mutateAsync({
          appointment_id: existing.id,
          delete_future_only: cancelScope === 'future',
          reason,
        });
        toast.success(t('appointments.toasts.series_deleted', { count }));
      } else {
        await cancel.mutateAsync({ appointment_id: existing.id, reason });
        toast.success(t('appointments.toasts.cancelled'));
      }
      onClose();
    } catch (err) {
      console.error('cancel appointment failed', err);
      toast.error(t('appointments.errors.unknown'));
    }
  }

  async function onComplete() {
    if (!existing) return;
    if (!confirm(t('appointments.modal.confirm_complete'))) return;
    try {
      await complete.mutateAsync(existing.id);
      toast.success(t('appointments.toasts.completed'));
      onClose();
    } catch (err) {
      console.error('complete failed', err);
      toast.error(t('appointments.errors.unknown'));
    }
  }

  async function onMarkNoShow() {
    if (!existing) return;
    if (!confirm(t('appointments.modal.confirm_no_show'))) return;
    try {
      await markNoShow.mutateAsync(existing.id);
      toast.success(t('appointments.toasts.no_show'));
      onClose();
    } catch (err) {
      console.error('mark no-show failed', err);
      toast.error(t('appointments.errors.unknown'));
    }
  }

  const pending =
    create.isPending ||
    createRecurring.isPending ||
    reschedule.isPending ||
    cancel.isPending ||
    complete.isPending ||
    markNoShow.isPending ||
    updateNotes.isPending ||
    deleteSeries.isPending ||
    updateSeries.isPending;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-black/50 p-4">
      <div className="mx-auto mt-10 max-w-xl rounded-xl bg-white shadow-xl dark:bg-card-dark">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            {mode === 'create'
              ? t('appointments.modal.new_title')
              : t('appointments.modal.edit_title')}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
            aria-label={t('common.close')}
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {loadingExisting ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4 p-6">
            {!isBlockerService && (
              <PatientPicker
                clinicId={clinicId}
                value={patient}
                onChange={setPatient}
                disabled={mode === 'edit'}
              />
            )}

            {isBlockerService && mode === 'create' && (
              <div className="rounded-md border border-blue-200 bg-blue-50 p-3 dark:border-blue-900/40 dark:bg-blue-900/10">
                <p className="text-sm text-blue-900 dark:text-blue-100">
                  {t('appointments.modal.blocker_info', 'Dette er en kalender blokering - ingen patient påkrævet')}
                </p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                {t('appointments.modal.service')}
              </label>
              <select
                value={serviceId ?? ''}
                onChange={(e) => applyServiceDuration(e.target.value || null)}
                className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              >
                <option value="">{t('appointments.modal.service_none')}</option>
                {services?.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} ({s.duration_minutes} {t('services.minutes_short')})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                {t('appointments.modal.clinician')}
              </label>
              <select
                value={clinicianId ?? ''}
                onChange={(e) => setClinicianId(e.target.value || null)}
                className="mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              >
                <option value="">{t('appointments.modal.clinician_unassigned')}</option>
                {members?.map((m) => (
                  <option key={m.user_id} value={m.user_id}>
                    {m.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Date/Time Selection Toggle */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                  {t('appointments.modal.datetime', 'Dato & Tid')}
                </label>
                <button
                  type="button"
                  onClick={() => setShowAvailabilityPicker(!showAvailabilityPicker)}
                  className="text-sm text-indigo-600 hover:text-indigo-700 dark:text-indigo-400"
                >
                  {showAvailabilityPicker 
                    ? t('appointments.modal.manual_entry', 'Manuel indtastning') 
                    : t('appointments.modal.show_availability', 'Vis ledige tider')}
                </button>
              </div>

              {showAvailabilityPicker ? (
                <AvailabilityPicker
                  clinicId={clinicId}
                  selectedClinicianId={clinicianId}
                  selectedDateTime={selectedDateTime}
                  duration={appointmentDuration}
                  excludeAppointmentId={mode === 'edit' ? appointmentId : null}
                  onSelect={handleAvailabilitySelect}
                  onClinicianChange={setClinicianId}
                />
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                      {t('appointments.modal.starts_at', 'Start')}
                    </label>
                    <input
                      type="datetime-local"
                      value={startsAt}
                      onChange={(e) => applyStart(e.target.value)}
                      required
                      className="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
                      {t('appointments.modal.ends_at', 'Slut')}
                    </label>
                    <input
                      type="datetime-local"
                      value={endsAt}
                      onChange={(e) => setEndsAt(e.target.value)}
                      required
                      className="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                {t('appointments.modal.notes')}
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder={t('appointments.modal.notes_placeholder') as string}
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
            </div>

            {mode === 'create' && (
              <RecurrenceForm value={recurrence} onChange={setRecurrence} />
            )}

            {showCancelConfirm && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-900/40 dark:bg-amber-900/10">
                <p className="text-sm font-medium text-amber-950 dark:text-amber-100">
                  {isRecurringSeries
                    ? t('appointments.recurrence.confirm_cancel_series')
                    : t('appointments.modal.confirm_cancel')}
                </p>
                {isRecurringSeries && (
                  <div className="mt-3 space-y-2">
                    <label className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200">
                      <input
                        type="radio"
                        name="cancelScope"
                        checked={cancelScope === 'single'}
                        onChange={() => setCancelScope('single')}
                      />
                      {t('appointments.recurrence.cancel_single')}
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200">
                      <input
                        type="radio"
                        name="cancelScope"
                        checked={cancelScope === 'all'}
                        onChange={() => setCancelScope('all')}
                      />
                      {t('appointments.recurrence.cancel_all')}
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200">
                      <input
                        type="radio"
                        name="cancelScope"
                        checked={cancelScope === 'future'}
                        onChange={() => setCancelScope('future')}
                      />
                      {t('appointments.recurrence.cancel_future')}
                    </label>
                  </div>
                )}
                <div className="mt-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    {t('appointments.modal.cancel_reason')}
                  </label>
                  <textarea
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                    rows={2}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div className="mt-4 flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setShowCancelConfirm(false)}
                    disabled={pending}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button type="button" variant="danger" onClick={onCancelConfirm} disabled={pending}>
                    {pending ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      t('appointments.modal.cancel_appointment')
                    )}
                  </Button>
                </div>
              </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-200 pt-4 dark:border-gray-700">
              <div>
                {canCancelAppointment && !showCancelConfirm && (
                  <Button type="button" variant="danger" onClick={openCancelConfirm} disabled={pending}>
                    <CalendarX className="h-4 w-4" />
                    {t('appointments.modal.cancel_appointment')}
                  </Button>
                )}
              </div>
              <div className="flex flex-wrap justify-end gap-2">
                {mode === 'edit' && existing?.status === 'upcoming' && !showCancelConfirm && (
                  <>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={onMarkNoShow}
                      disabled={pending}
                    >
                      {t('appointments.modal.no_show_appointment')}
                    </Button>
                    <Button type="button" variant="secondary" onClick={onComplete} disabled={pending}>
                      {t('appointments.modal.complete_appointment')}
                    </Button>
                  </>
                )}
                <Button type="button" variant="secondary" onClick={onClose}>
                  {t('common.close')}
                </Button>
                {!showCancelConfirm && (
                  <Button type="submit" disabled={pending}>
                    {pending
                      ? t('common.saving')
                      : mode === 'create'
                        ? t('appointments.modal.save_create')
                        : t('appointments.modal.save_update')}
                  </Button>
                )}
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PatientPicker — typeahead used inside the modal. When `disabled` is true
// (edit mode), shows the locked patient as plain text.
// ---------------------------------------------------------------------------
function PatientPicker({
  clinicId,
  value,
  onChange,
  disabled,
}: {
  clinicId: string;
  value: PatientPickerItem | null;
  onChange: (next: PatientPickerItem | null) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const { data: results } = usePatientPicker(clinicId, search);
  const list = useMemo(() => results ?? [], [results]);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  if (disabled && value) {
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
          {t('appointments.modal.patient')}
        </label>
        <div className="mt-1 rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200">
          {value.first_name || value.last_name
            ? `${value.first_name} ${value.last_name}`.trim()
            : value.id}
        </div>
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className="relative">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
        {t('appointments.modal.patient')}
      </label>
      {value ? (
        <div className="mt-1 flex items-center justify-between rounded-md border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700">
          <span className="text-gray-900 dark:text-white">
            {value.first_name} {value.last_name}
            {value.pat_number && (
              <span className="ml-2 font-mono text-xs text-gray-500 dark:text-gray-400">
                #{value.pat_number}
              </span>
            )}
          </span>
          <button
            type="button"
            onClick={() => {
              onChange(null);
              setSearch('');
            }}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            aria-label={t('common.delete')}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <>
          <div className="relative mt-1">
            <Search className="pointer-events-none absolute inset-y-0 left-3 my-auto h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              placeholder={t('appointments.modal.patient_search_placeholder') as string}
              className="block w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
          </div>
          {open && list.length > 0 && (
            <ul className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800">
              {list.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onChange(p);
                      setSearch('');
                      setOpen(false);
                    }}
                    className="flex w-full flex-col items-start px-3 py-2 text-left text-sm hover:bg-indigo-50 dark:hover:bg-indigo-900/30"
                  >
                    <span className="font-medium text-gray-900 dark:text-white">
                      {p.first_name} {p.last_name}
                    </span>
                    {(p.email || p.phone) && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {p.email || p.phone}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
